import { dropTargetForElements } from "@atlaskit/pragmatic-drag-and-drop/element/adapter";
import { zodResolver } from "@hookform/resolvers/zod";
import { Immutable, castImmutable } from "immer";
import { atom, useAtomValue, useStore } from "jotai";
import { Trash } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { useMutation, useQuery } from "urql";
import { useMemoOne } from "use-memo-one";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { type LayoutTemplateQuery } from "@/gen/graphql";
import { CmsBlock, CmsLayout } from "@/gen/realtime";
import { ContentEditor } from "@/routes/layouts/components/ContentEditor";
import { LayoutEditSidebar } from "@/routes/layouts/components/Sidebar";
import {
    type TemplateMetadata,
    TemplateMetadataForm,
} from "@/routes/layouts/components/TemplateMetadataForm";
import { useDragAndDrop } from "@/routes/layouts/dragAndDrop";
import { setupKeyboardHandlers } from "@/routes/layouts/keyboardHandlers";
import { Action, initialState, updateState } from "@/routes/layouts/layoutEditState";
import { UndoHandlerProvider, useUndoHandler } from "@/routes/layouts/undoHandler";
import { cn } from "@/utils";

import { layoutTemplateQuery, updateLayoutTemplateMutation } from "./queries/layoutTemplates";

const templateMetadataSchema = z.object({
    name: z.string().min(1, "Name is required"),
    isDefault: z.boolean(),
});

export default function LayoutTemplateDetailRoute() {
    const { templateId } = useParams<{ templateId: string }>();

    const [result] = useQuery({
        query: layoutTemplateQuery,
        variables: {
            id: templateId!,
        },
    });

    if (result.error) {
        return <div>{result.error.toString()}</div>;
    }

    if (!result.data) {
        return <div>Loading...</div>;
    }

    if (!result.data.tournamentLayoutTemplate) {
        return <div>Template not found</div>;
    }

    return (
        <UndoHandlerProvider>
            <LayoutTemplateDetailView template={result.data.tournamentLayoutTemplate} />
        </UndoHandlerProvider>
    );
}

function LayoutTemplateDetailView({
    template,
}: {
    template: NonNullable<LayoutTemplateQuery["tournamentLayoutTemplate"]>;
}) {
    const metadataForm = useForm<TemplateMetadata>({
        defaultValues: {
            name: template.name,
            isDefault: template.isDefault,
        },
        mode: "onSubmit",
        resolver: zodResolver(templateMetadataSchema),
    });

    const [_updateTemplateRes, updateTemplate] = useMutation(updateLayoutTemplateMutation);

    const { onPatches, undo, flushChanges } = useUndoHandler();

    const store = useStore();
    const stateAtom = useMemoOne(() => atom(castImmutable(initialState())), []);
    const state = useAtomValue(stateAtom);

    const dispatch = useCallback(
        (action: Action) => {
            const oldValue = store.get(stateAtom);
            const newValue = updateState(oldValue, action, store, onPatches);
            store.set(stateAtom, newValue);
            flushChanges();
        },
        [store, stateAtom, onPatches, flushChanges]
    );

    // Setup keyboard handlers (undo/redo, copy/paste)
    useEffect(() => {
        return setupKeyboardHandlers({
            store,
            stateAtom,
            dispatch,
            undo,
        });
    }, [dispatch, stateAtom, store, undo]);

    // Setup drag and drop
    const { isDragging } = useDragAndDrop(dispatch);

    // Load initial content
    useEffect(() => {
        if (template.content) {
            dispatch({
                type: "set-content",
                content: template.content as CmsLayout,
            });
        }
    }, [template.content, dispatch]);

    const save = () => {
        metadataForm.handleSubmit(async metadata => {
            const blocks: Record<string, Immutable<CmsBlock>> = {};

            for (const blockId of Object.keys(state.layout.blocks)) {
                blocks[blockId] = store.get(state.layout.blocks[blockId]!);
            }

            const content: Immutable<CmsLayout> = { ...state.layout, blocks };
            const result = await updateTemplate({
                input: {
                    id: template.id,
                    ...metadata,
                    content,
                },
            });

            if (result.error) {
                toast.error("Failed to save template", {
                    description: result.error.message,
                });
            } else {
                toast.success("Template saved successfully");
            }
        })();
    };

    if (!state) {
        return <div>Loading...</div>;
    }

    const phaseLabel =
        template.tournamentPhase === "BEFORE"
            ? "Before Tournament"
            : template.tournamentPhase === "DURING"
              ? "During Tournament"
              : "After Tournament";

    return (
        <div>
            <div className="mx-4 flex items-center gap-3">
                <Button onClick={save}>Save</Button>
                {isDragging && <DragToDelete />}
                <Button
                    onClick={() => {
                        dispatch({
                            type: "set-active-block",
                            blockId: null,
                        });
                    }}
                    className="ml-auto"
                >
                    Template Settings
                </Button>
            </div>
            <div className="m-2 flex flex-row gap-4">
                <ContentEditor
                    dispatch={dispatch}
                    state={state}
                    blocks={state.layout.order}
                    depth={0}
                    parentId={null}
                    allParents={[]}
                />
                <LayoutEditSidebar
                    state={state}
                    dispatch={dispatch}
                    onSubmit={save}
                    customMetadataComponent={
                        <TemplateMetadataForm
                            form={metadataForm}
                            onSubmit={save}
                            phaseLabel={phaseLabel}
                        />
                    }
                />
            </div>
        </div>
    );
}

const DragToDelete = () => {
    const element = useRef<HTMLDivElement>(null);
    const [active, setActive] = useState(false);
    useEffect(() => {
        return dropTargetForElements({
            element: element.current!,
            getData() {
                return { t: "delete" };
            },
            onDragEnter: () => {
                setActive(true);
            },
            onDragLeave() {
                setActive(false);
            },
            onDrop() {
                setActive(false);
            },
        });
    });

    return (
        <div
            ref={element}
            className={cn(
                "mx-4 flex items-center gap-3 rounded bg-red-300 p-2",
                active ? "bg-red-600" : "bg-red-300"
            )}
        >
            <Trash /> Drag here to delete
        </div>
    );
};
